import React, { useEffect, useMemo, useRef, ReactNode, useCallback } from 'react';
import { NodeProps, NodeResizer, Position, Handle, useReactFlow, useStore } from '@xyflow/react';
import { useSelector } from 'react-redux';
import { TbTrash, TbArrowsMaximize, TbArrowsMinimize } from 'react-icons/tb';
import { FiMaximize, FiLock } from 'react-icons/fi';
import { runStatusLabel, NodeRunStatus, formatDuration } from '../utils/runStatus';
import styles from './WorkflowNode.module.css';
import { useTranslation } from 'react-i18next';
import { typeLabelOf } from '../nodeCatalog';

// Define the proper NodeData interface that matches the project
interface NodeData {
  label?: ReactNode;
  customId?: string;
  isDragging?: boolean;
  [key: string]: any;
}

/**
 * 모든 그룹 노드 타입의 기본 구조를 제공하는 BaseGroupNode 컴포넌트
 * React Flow 표준 방식으로 구현
 */
const BaseGroupNode = ({ id, data, type, selected, isConnectable }: NodeProps) => {
  const { t } = useTranslation();
  // 그룹 노드의 DOM 요소 참조
  const nodeRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // ReactFlow 인스턴스 접근
  const { setNodes, getNodes, deleteElements } = useReactFlow();

  /** 분할처리가 만든 그룹 — 안쪽 배치·규칙이 자동 생성이라 크기 조절·삭제를 막고 자물쇠를 보여 준다 */
  const readOnly = (data as any)?.readOnly === true;
  /** 접힘 — 자동 생성 그룹은 기본이 접힘이다. 한 노드처럼 보이고, 펼쳐야 안쪽 흐름이 보인다 */
  const collapsed = (data as any)?.collapsed !== false && readOnly;

  // 그룹도 실행 상태·소요시간을 보여 준다 — 값은 자식들의 상태를 요약한 것이다(utils/runStatus)
  const groupRunStatus = useSelector((s: any) => s.workflow?.runStatus?.[id] as NodeRunStatus | undefined);
  const groupRunDuration = useSelector((s: any) => s.workflow?.runDuration?.[id] as number | undefined);
  
  // 노드 타입별 최소 크기 (자동 모드에서 자식이 없거나 작을 때의 크기)
  const minSize = useMemo(() => {
    switch (type) {
      case 'subflow': return { width: 320, height: 240 };
      case 'repeatGroup': return { width: 280, height: 220 };
      default: return { width: 300, height: 240 };
    }
  }, [type]);

  // ── 크기 모드: 자동(기본) = 자식 노드 배치에 맞춰 늘고 줄어듦 / 수동 = 사용자가 리사이저로 정한 크기 유지.
  //    data.autoSize 로 캔버스 JSON 에 함께 저장된다.
  const autoSize = data?.autoSize !== false;
  const HEADER_H = 30, PAD = 24;
  // 자식 노드 배치의 경계 (부모 기준 상대 좌표) — 위치/크기가 바뀔 때만 재계산되도록 문자열 키로 구독
  const childBoundsKey = useStore(
    (s) => s.nodes.filter(n => n.parentId === id).map(n => `${n.id}:${Math.round(n.position.x)},${Math.round(n.position.y)},${Math.round(n.measured?.width ?? Number((n.style as any)?.width) ?? 150)},${Math.round(n.measured?.height ?? Number((n.style as any)?.height) ?? 80)}`).join('|'),
  );
  const fitted = useMemo(() => {
    if (!childBoundsKey) return { ...minSize };
    let maxX = 0, maxY = 0;
    childBoundsKey.split('|').forEach(part => {
      const xywh = part.slice(part.indexOf(':') + 1);
      const [x, y, w, h] = xywh.split(',').map(Number);
      maxX = Math.max(maxX, x + (w || 150));
      maxY = Math.max(maxY, y + (h || 80));
    });
    return { width: Math.max(minSize.width, Math.round(maxX + PAD)), height: Math.max(minSize.height, Math.round(maxY + PAD + HEADER_H)) };
  }, [childBoundsKey, minSize]);

  // 현재 노드 크기 (수동 모드에서는 저장된 값, 없으면 최소 크기)
  const storedSize = useStore(
    (s) => { const n = s.nodes.find(n => n.id === id); return n ? { width: (n.style as any)?.width ?? n.width, height: (n.style as any)?.height ?? n.height } : undefined; },
    (a, b) => a?.width === b?.width && a?.height === b?.height,
  );
  // 안의 노드를 끄는 동안에는 크기를 고정한다 — 따라 커지면 밖으로 꺼내려는 노드를 그룹이 계속 덮어
  // 노드가 딸려 오는 것처럼 보였다(현장 2026-10-12). 놓은 뒤(그룹에 남았으면) 그때 맞춘다.
  const childDragging = useStore((s) => s.nodes.some((n) => n.parentId === id && n.dragging));
  const frozen = childDragging && Number(storedSize?.width) > 0 && Number(storedSize?.height) > 0;
  const dimensions = autoSize && !frozen
    ? fitted
    : { width: Number(storedSize?.width) || minSize.width, height: Number(storedSize?.height) || minSize.height };

  const applySize = useCallback((w: number, h: number, extraData?: Record<string, any>) => {
    setNodes((nodes) => nodes.map((node) => node.id === id
      ? { ...node, style: { ...(node.style || {}), width: w, height: h }, width: w, height: h, ...(extraData ? { data: { ...node.data, ...extraData } } : {}) }
      : node));
  }, [id, setNodes]);

  // 자동 모드: 맞춘 크기가 저장된 크기와 다르면 반영 (자식 추가/이동/삭제에 따라 늘고 줄어든다)
  useEffect(() => {
    if (!autoSize || childDragging) return;
    if (Number(storedSize?.width) === fitted.width && Number(storedSize?.height) === fitted.height) return;
    applySize(fitted.width, fitted.height);
  }, [autoSize, childDragging, fitted.width, fitted.height, storedSize?.width, storedSize?.height, applySize]);

  // 리사이저로 크기를 바꾸면 수동 모드로
  const onResizeEnd = useCallback((_: any, params: { width: number; height: number }) => {
    applySize(Math.round(params.width), Math.round(params.height), { autoSize: false });
  }, [applySize]);

  // 자동 ↔ 수동 전환 (자동으로 돌리면 즉시 자식에 맞춰 재조정)
  const toggleAutoSize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (autoSize) applySize(dimensions.width, dimensions.height, { autoSize: false });
    else applySize(fitted.width, fitted.height, { autoSize: true });
  }, [autoSize, dimensions.width, dimensions.height, fitted.width, fitted.height, applySize]);

  // 노드 타입별 스타일 정의
  const nodeStyle = useMemo(() => {
    const baseStyle: React.CSSProperties = {
      width: dimensions.width,
      height: dimensions.height,
      backgroundColor: 'rgb(var(--c-surface))',
      border: '1px solid #ccc',
      borderRadius: '8px',
      position: 'relative',
      boxSizing: 'border-box' as 'border-box',
      padding: 0,
      overflow: 'visible',
      zIndex: 0
    };
    
    // 노드 타입별 스타일 차별화
    if (type === 'subflow') {
      baseStyle.backgroundColor = 'rgb(var(--c-surface))'; // 일반 노드(bg-white)와 같은 표면색 — 다크에서는 --c-surface 가 바뀐다
      baseStyle.border = '2px solid #9dccbd';
    } else if (type === 'repeatGroup') {
      baseStyle.backgroundColor = 'rgb(var(--c-surface))'; // 일반 노드(bg-white)와 같은 표면색 — 다크에서는 --c-surface 가 바뀐다
      baseStyle.border = '2px solid #d9bebe';
    } else {
      baseStyle.backgroundColor = 'rgb(var(--c-surface))'; // 일반 노드(bg-white)와 같은 표면색 — 다크에서는 --c-surface 가 바뀐다
      baseStyle.border = '2px solid #aab7c4';
    }
    
    // 선택 상태일 때 스타일 강조
    // 테두리 색만 바꾼다 — 예전엔 2px 그림자를 테두리 위에 겹쳐 4px 넘게 두꺼웠다(현장 2026-10-01)
    if (selected) {
      baseStyle.borderColor = '#3772FF';
    }
    
    // 드래그 상태일 때 스타일 변경
    if (data?.isDragging) {
      baseStyle.opacity = '0.8';
      baseStyle.boxShadow = '0 0 8px rgba(255, 153, 0, 0.5)';
    }
    
    return baseStyle;
  }, [dimensions.width, dimensions.height, type, selected, data?.isDragging]);
  
  // 헤더 스타일 정의
  const headerStyle = useMemo(() => ({
    width: '100%',
    height: '30px',
    backgroundColor: type === 'subflow' ? '#9dccbd' : 
                    type === 'repeatGroup' ? '#d9bebe' : 
                    '#aab7c4',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '5px 10px',
    boxSizing: 'border-box' as 'border-box',
    fontWeight: 'bold',
    fontSize: '12px',
    color: '#333'
  }), [type]);
  
  // 콘텐츠 영역 스타일 정의
  const contentStyle = useMemo(() => ({
    width: '100%',
    height: 'calc(100% - 30px)', // 헤더 높이 제외
    padding: '10px',
    boxSizing: 'border-box' as 'border-box',
    position: 'relative' as const,
    overflow: 'visible',
    backgroundColor: 'transparent',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px'
  }), []);
  
  // 그룹 노드 초기화 및 디버깅 정보
  // 헤더 텍스트 (라벨)
  const label = data?.label || (
    type === 'subflow' ? t('workflow:group.subflow') :
    type === 'repeatGroup' ? t('workflow:group.repeatGroup') :
    t('workflow:group.group')
  );

  // Helper function to safely render content that might not be ReactNode
  const safeRender = (content: any): ReactNode => {
    if (content === null || content === undefined) return null;
    if (typeof content === 'string' || typeof content === 'number' || typeof content === 'boolean') {
      return String(content);
    }
    if (React.isValidElement(content)) {
      return content;
    }
    return String(content);
  };

  // 노드 삭제 함수
  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    deleteElements({ nodes: [{ id }] });
  };

  return (
    <div
      ref={nodeRef}
      className={`group-node ${type}-node ${selected ? 'selected' : ''} ${data?.isDragging ? 'dragging' : ''}`}
      style={collapsed ? {
        // 접으면 한 노드처럼 — 그룹의 점선 상자가 아니라 다른 노드와 같은 흰 상자로 보여야 한다
        ...nodeStyle,
        width: 105, height: 56,
        border: `2px solid ${selected ? '#3b82f6' : 'rgb(var(--c-gray-200))'}`,
        borderRadius: 8,
        backgroundColor: 'rgb(var(--c-surface))',
        boxShadow: '0 1px 3px rgba(0,0,0,.12)',
        padding: 0,
      } : nodeStyle}
      data-node-id={id}
      data-node-type={type}
      data-dimensions={`${dimensions.width}x${dimensions.height}`}
    >
      {/* 실행 상태·소요시간 — 그룹은 자식들의 상태를 요약해 받는다 (utils/runStatus) */}
      {groupRunStatus && (
        <span className={`run-badge run-badge--${groupRunStatus} nodrag`}
          title={`${t('workflow:node.runStatusTitle', { status: runStatusLabel(groupRunStatus) })}${groupRunDuration !== undefined ? t('workflow:node.durationPart', { duration: formatDuration(groupRunDuration) }) : ''}${t('workflow:node.clickForLog')}`}
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openflow:open-node-log', { detail: { nodeId: id } })); }}>
          {groupRunStatus === 'running' && <span className="run-badge__spin" />}
          {runStatusLabel(groupRunStatus)}
          {groupRunDuration !== undefined && groupRunStatus !== 'running' && groupRunStatus !== 'waiting' && (
            <span className="run-badge__dur">{formatDuration(groupRunDuration)}</span>
          )}
        </span>
      )}

      {/* 우상단 아이콘 줄 — 다른 노드와 같은 자리. 접기/펼치기, 그리고 삭제(자동 생성 그룹은 자물쇠) */}
      <div style={{ position: 'absolute', top: -24, right: -14, zIndex: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        {readOnly && (
          <span
            className="nodrag"
            role="button"
            title={collapsed ? t('workflow:group.expand') : t('workflow:group.collapse')}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openflow:toggle-group-collapse', { detail: { nodeId: id } })); }}
            style={{ display: 'inline-flex', color: '#888', cursor: 'pointer' }}
          >
            {/* 펼치기/접기 — 네 방향 화살표가 '넓게 펴기 / 접어 모으기' 를 글자보다 분명하게 보여 준다.
                (이 저장소의 react-icons 타입이 React 18 과 맞지 않아 다른 노드처럼 함수로 부른다) */}
            {collapsed ? TbArrowsMaximize({ size: 14 }) : TbArrowsMinimize({ size: 14 })}
          </span>
        )}
        {readOnly ? (
          <span title={t('workflow:group.splitLocked')}
            style={{ fontSize: 12, color: '#9ca3af', cursor: 'default' }}>🔒</span>
        ) : (
          TbTrash({
            className: styles.trashIcon,
            onClick: handleDelete,
            title: t('workflow:node.delete')
          })
        )}
      </div>

      {/* 노드 크기 조절기 — 분할처리가 만든 그룹은 크기·배치가 규칙의 일부라 조절하지 않는다 */}
      <NodeResizer
        minWidth={200}
        minHeight={120}
        isVisible={selected && !readOnly}
        handleClassName="of-resize-corner"
        lineStyle={{ background: '#3772FF' }}
        onResizeEnd={onResizeEnd}
      />
      
      {/* ID 는 속성창 머리에 보인다 — 캔버스 위에 띄우던 「이름 (ID: …)」 표시는 뺐다(현장 2026-10-01) */}
      {/* 노드 헤더 — 접으면 감춘다. 한 노드처럼 보여야 하는데 제목줄이 남으면 상자 안에 글자가 겹친다 */}
      <div className="group-header" style={{ ...headerStyle, ...(collapsed ? { display: 'none' } : {}) }}>
        <div className="header-title">
          <span>{safeRender(label)}</span>
        </div>
        <button
          type="button"
          className="nodrag"
          onClick={toggleAutoSize}
          onMouseDown={(e) => e.stopPropagation()}
          title={autoSize ? t('workflow:group.autoSize') : t('workflow:group.manualSize')}
          style={{ pointerEvents: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'inline-flex', color: autoSize ? '#3b82f6' : '#f59e0b' }}
        >
          {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
          {autoSize ? <FiMaximize size={13} /> : <FiLock size={13} />}
        </button>
      </div>
      
      {/* 접힘일 때의 명칭 — 다른 노드와 같이 상자 밖 아래 한 줄로. 접으면 머리줄이 사라져 이름이 보이지 않기 때문 */}
      {collapsed && (
        <div
          className="nodrag"
          title={typeof label === 'string' ? label : undefined}
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 7,
            maxWidth: 170, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            // 다른 노드의 명칭과 같은 크기 — 그룹만 작으면 한 줄로 늘어선 노드들의 글자가 들쭉날쭉해진다
            fontSize: '12px', lineHeight: '1rem', fontWeight: 500, textAlign: 'center',
            color: 'rgb(var(--c-gray-800))', pointerEvents: 'none',
            textShadow: '0 0 3px rgb(var(--c-gray-50)), 0 0 3px rgb(var(--c-gray-50))',
          }}
        >
          {safeRender(label)}
        </div>
      )}

      {/* 접힘 — 한 노드처럼 보이게 아이콘과 유형명만. 안쪽 흐름은 펼치기 아이콘으로 연다 */}
      {collapsed && (
        <div className="flex items-center justify-center gap-1.5 w-full h-full px-1 pointer-events-none">
          <span className="shrink-0 text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M4 7h11" /><path d="M12 4l3 3-3 3" /><path d="M20 17H9" /><path d="M12 14l-3 3 3 3" />
            </svg>
          </span>
          <span className="min-w-0 flex flex-col items-start">
            <span className="text-xs font-medium text-gray-900 leading-tight">{typeLabelOf('transformLoad')}</span>
            <span className="text-[0.5625rem] text-gray-400 leading-none">
              {t('workflow:group.splitCount', { n: (data as any)?.split?.count ?? '' })}
            </span>
          </span>
        </div>
      )}

      {/* 노드 콘텐츠 영역 - 자식 노드가 이 영역에 배치됨 */}
      <div
        ref={contentRef}
        className="group-content"
        style={{ ...contentStyle, ...(collapsed ? { display: 'none' } : {}) }}
        data-content-for={id}
      >
        {/* 개발 모드에서 중심점 표시 */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '10px',
            height: '10px',
            backgroundColor: 'rgba(255, 0, 0, 0.3)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />
        )}
        
        {/* 자식 노드는 React Flow에 의해 자동으로 이 영역에 렌더링됨 */}
      </div>
      
      {/* 연결 핸들 */}
      {/* 접으면 한 노드처럼 보이므로 연결점도 다른 노드와 같은 좌(입력)·우(출력)에 둔다 —
          위/아래에 두면 소스 → 그룹 → 타깃 선이 위로 크게 돌아간다.
          색·크기·위치는 일반 노드와 같게 CSS(.react-flow__handle)와 React Flow 기본값에 맡긴다 —
          여기서 transform 을 주면 핸들이 경계에 걸치지 않고 안으로 들어간다. */}
      <Handle
        type="target"
        position={collapsed ? Position.Left : Position.Top}
        isConnectable={isConnectable}
      />
      <Handle
        type="source"
        position={collapsed ? Position.Right : Position.Bottom}
        isConnectable={isConnectable}
      />
    </div>
  );
};

// 디스플레이 이름 설정
BaseGroupNode.displayName = 'BaseGroupNode';

export default React.memo(BaseGroupNode); 